import { lazy, type ReactNode, Suspense, useMemo } from "react";
import { IntlProvider } from "react-intl";
import { type Location, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthActionPage } from "../auth/AuthAction";
import { SignInScreen } from "../auth/AuthGate";
import { ApiError } from "../core/api-client";
import { resolveMessages } from "../core/messages";
import { SECTIONS } from "../core/sections";
import { ProfilePage } from "../features/profiles/ProfilePage";
import { TemplateDetailRoute } from "../features/templates/detail/TemplateDetailRoute";
import { TemplatesPage } from "../features/templates/TemplatesPage";
import { LegalPage } from "../public/LegalPage";
import { NotFoundPage } from "../public/NotFoundPage";
import { PricingPage } from "../public/PricingPage";
import { PublicShell } from "../public/PublicShell";
import { PublicTemplateDetailPage } from "../public/PublicTemplateDetailPage";
import { SplashPage } from "../public/SplashPage";
import { ErrorState, LoadingState } from "../shared/Shared";
import { useAppContext } from "./AppContext";
import { AppShell } from "./AppShell";
import { ComingNext } from "./ComingNext";
import { lazyRoute } from "./lazy-route";
import { ReleaseBanner } from "./ReleaseBanner";

// Route-level code splitting (Section 1 frame hardening): the public pages — splash,
// pricing, the gallery, template detail, sign-in — never download the member surfaces.
// The MFA-only splash was the first lazy route; the member routes below follow the same
// idea through `lazyRoute`, which keeps a dropped chunk off the root boundary.
const MfaSplashPage = lazy(() => import("../public/MfaSplashPage"));
const BuilderPage = lazyRoute(() =>
  import("../features/builder/BuilderPage").then((module) => module.BuilderPage),
);
const MfaBuilderGateway = lazyRoute(() =>
  import("../features/builder/BuilderGateway").then((module) => module.MfaBuilderGateway),
);
const StarterWizardPage = lazyRoute(() =>
  import("../features/wizard/StarterWizardPage").then((module) => module.StarterWizardPage),
);
const HistoryPage = lazyRoute(() =>
  import("../features/history/HistoryPage").then((module) => module.HistoryPage),
);
const BillingPage = lazyRoute(() =>
  import("../features/billing/BillingPage").then((module) => module.BillingPage),
);
const AdminDashboardPage = lazyRoute(() =>
  import("../features/admin/AdminDashboardPage").then((module) => module.AdminDashboardPage),
);
// Dev only: ONE dashboard for every dev tool (Overview · RPC · Runs tabs), mounted at
// dev/* in BOTH trees when runtime config carries `devTools` (the server publishes it only
// with DEV_TOOLS_ENABLED, refused in prod). Deliberately NOT a member deep-link pattern:
// without the flag every /dev path is the real not-found page everywhere.
const DevToolsDashboard = lazyRoute(() =>
  import("../features/devtools/DevToolsDashboard").then((module) => module.DevToolsDashboard),
);
// The public API reference (V6 plan row H.22, decision D65): every procedure and route the API
// serves, the OpenAPI document and the quickstarts, at /developers in BOTH trees on both sites.
// Lazy like the member routes: the contract's walk and its schemas are paid for on that page only.
const DevelopersPage = lazyRoute(() =>
  import("../features/developers/DevelopersPage").then((module) => module.DevelopersPage),
);

/**
 * Member-only paths a signed-out visitor may deep-link to. The public tree renders the
 * sign-in screen IN PLACE for these (no redirect) so the URL survives the exchange and
 * the member tree resolves it afterwards. Anything else unknown is a real not-found.
 */
export const MEMBER_DEEP_LINK_PATTERNS = [
  "dashboard",
  "profile",
  "start",
  "history",
  "billing",
  "marketplace",
  "projects/*",
  "templates/community/*",
  "admin/*",
] as const;

export function App() {
  const { locale, loading, viewer, experience, bootstrapSiteId, bootstrapError, refreshBootstrap } =
    useAppContext();
  const messages = useMemo(() => resolveMessages(locale), [locale]);
  let content: ReactNode;
  if (loading) {
    content = <LoadingState />;
  } else if (
    bootstrapError &&
    !(bootstrapError instanceof ApiError && bootstrapError.status === 401)
  ) {
    content = <ErrorState onRetry={() => void refreshBootstrap()} />;
  } else if (bootstrapSiteId && bootstrapSiteId !== experience.id) {
    content = <ErrorState titleId="site.mismatchTitle" bodyId="site.mismatchBody" />;
  } else if (!viewer) {
    content = <PublicApp />;
  } else if (viewer.siteId !== experience.id) {
    content = <ErrorState titleId="site.mismatchTitle" bodyId="site.mismatchBody" />;
  } else {
    content = <MemberApp />;
  }
  return (
    <IntlProvider
      locale={locale}
      messages={messages}
      onError={(error) => {
        if (import.meta.env.DEV) console.error(error);
      }}
    >
      <ReleaseBanner />
      {content}
    </IntlProvider>
  );
}

/**
 * Signed-out tree (PHASE1_PLAN.md stage 3.2): the V1-style public surface —
 * splash, pricing, template browsing, sign-in, the legal pages. Member paths
 * render the sign-in screen IN PLACE (no redirect), so deep links survive the
 * exchange: once the session exists the member tree resolves the original URL.
 */
function PublicApp() {
  // Both sites render the sign-in card INSIDE the public shell (the owner
  // design shows the public header above the card; LYA's full-bleed
  // two-panel screen retired with the 2026-07 corporate rebuild). Deep links
  // still resolve in place on both sites.
  const { experience, runtimeConfig } = useAppContext();
  // MFA-only splash replacement (owner spec 2026-07-26). Site-branched, never
  // URL-branched; LYA keeps the shared SplashPage.
  const splash =
    experience.id === "myfirstagent" ? (
      <Suspense fallback={<LoadingState />}>
        <MfaSplashPage />
      </Suspense>
    ) : (
      <SplashPage />
    );
  return (
    <Routes>
      <Route element={<PublicShell />}>
        <Route index element={splash} />
        <Route path="pricing" element={<PricingPage />} />
        {/* One template gallery for everyone (owner decision 2026-07-27): the
            member TemplatesPage renders here too, in anonymous mode. */}
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="templates/:libraryKey/:slug" element={<PublicTemplateDetailPage />} />
        <Route path="privacy" element={<LegalPage document="privacy" />} />
        <Route path="terms" element={<LegalPage document="terms" />} />
        <Route path="developers" element={<DevelopersPage />} />
        <Route path="login" element={<SignInScreen />} />
        <Route path="register" element={<SignInScreen />} />
        <Route path="signup" element={<Navigate to="/register" replace />} />
        <Route path="auth/action" element={<AuthActionPage />} />
        {/* Dev tools answer signed out too, so anonymous calls can be tested. */}
        {runtimeConfig.devTools ? <Route path="dev/*" element={<DevToolsDashboard />} /> : null}
        {MEMBER_DEEP_LINK_PATTERNS.map((pattern) => (
          <Route key={pattern} path={pattern} element={<SignInScreen />} />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function MemberApp() {
  const location = useLocation();
  const { runtimeConfig } = useAppContext();
  // A gallery card pushes the canonical detail URL with the gallery location
  // as `state.background`: the main tree keeps rendering the gallery while the
  // overlay tree renders the detail as the V1 modal. Direct loads and
  // refreshes carry no background, so the same route renders standalone.
  const background = (location.state as { background?: Location } | null)?.background;
  return (
    <>
      <Routes location={background ?? location}>
        <Route element={<AppShell />}>
          <Route index element={<IndexRedirect />} />
          {/* Section 1 interim (SECTIONS.projects): the builder, the wizard, the dashboard
              gateway and run history arrive with projects; until then each renders the
              ComingNext panel — the one-line reverts the plan promised. */}
          <Route
            path="dashboard"
            element={SECTIONS.projects ? <MfaBuilderGateway /> : <ComingNext feature="builder" />}
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="templates" element={<TemplatesPage />} />
          {/* Section 1 interim (SECTIONS.marketplace): community templates have no API in
              this build, and their pages went with the client that called no server route
              (workflows/V5-Template-Method-Review.md V5); the deep links keep the panel. */}
          <Route path="marketplace" element={<ComingNext feature="marketplace" />} />
          <Route
            path="start"
            element={SECTIONS.projects ? <StarterWizardPage /> : <ComingNext feature="wizard" />}
          />
          {/* Community detail is UUID-addressed on BOTH brands — private
              slugs are only library-unique, so it never rides the
              libraryKey/slug route below. */}
          <Route
            path="templates/community/:templateId"
            element={<ComingNext feature="marketplace" />}
          />
          <Route
            path="templates/:libraryKey/:slug"
            element={<TemplateDetailRoute variant="standalone" />}
          />
          <Route
            path="history"
            element={SECTIONS.projects ? <HistoryPage /> : <ComingNext feature="history" />}
          />
          <Route path="billing" element={<BillingPage />} />
          <Route path="pricing" element={<Navigate to="/billing" replace />} />
          <Route path="privacy" element={<LegalPage document="privacy" />} />
          <Route path="terms" element={<LegalPage document="terms" />} />
          <Route path="developers" element={<DevelopersPage />} />
          <Route path="login" element={<PostAuthRedirect />} />
          <Route path="register" element={<PostAuthRedirect />} />
          <Route path="signup" element={<PostAuthRedirect />} />
          {/* Signed-in users open verification/reset links too. */}
          <Route path="auth/action" element={<AuthActionPage />} />
          <Route
            path="projects/:projectId"
            element={SECTIONS.projects ? <BuilderPage /> : <ComingNext feature="builder" />}
          />
          {runtimeConfig.devTools ? <Route path="dev/*" element={<DevToolsDashboard />} /> : null}
          {/* The admin dashboard (SECTIONS.admin). Every OTHER admin path is still the
              panel — the costing sheet waits for Section 2 — never a not-found. */}
          {SECTIONS.admin ? (
            <Route
              path="admin"
              element={
                <AdminOnly>
                  <AdminDashboardPage />
                </AdminOnly>
              }
            />
          ) : null}
          <Route
            path="admin/*"
            element={
              <AdminOnly>
                <ComingNext feature="admin" />
              </AdminOnly>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      {background ? (
        <Routes>
          <Route
            path="templates/:libraryKey/:slug"
            element={<TemplateDetailRoute variant="modal" />}
          />
          <Route path="*" element={null} />
        </Routes>
      ) : null}
    </>
  );
}

function AdminOnly({ children }: { children: ReactNode }) {
  // Presentation-only gate (bootstrap admin surface); the server re-authorizes
  // every /api/v5/admin request independently.
  const { admin } = useAppContext();
  return admin ? children : <Navigate to="/" replace />;
}

function PostAuthRedirect() {
  const location = useLocation();
  // Sign-in landed with a preserved pricing selection: hand the OPAQUE offer
  // id to the billing page, which fires the server-authorized selection. The
  // param carries intent only — never entitlement.
  const offerId = new URLSearchParams(location.search).get("offer");
  return offerId ? (
    <Navigate to={`/billing?offer=${encodeURIComponent(offerId)}`} replace />
  ) : (
    <Navigate to="/" replace />
  );
}

function IndexRedirect() {
  const location = useLocation();
  // Both sites land straight on Templates + Start; a deep link's query survives. This is
  // also the Section 1 landing while SECTIONS.projects is off: never /dashboard, whose
  // gateway would need the projects API.
  return <Navigate to={`/templates${location.search}`} replace />;
}
